import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Field, Input, Select } from "../components/kr";
import { toast } from "sonner";
import { 
  FloppyDisk, 
  Sliders, 
  Palette, 
  Coins, 
  ShieldCheck, 
  Receipt, 
  Trash, 
  Plus, 
  FileText,
  Package
} from "@phosphor-icons/react";

const THEMES = {
  red: { name: "Earth's Red", primary: "#B91C1C", dark: "#7F1D1D", bg: "#FAF7F2" },
  orange: { name: "Industrial Orange", primary: "#EA580C", dark: "#C2410C", bg: "#F4F4F5" },
  blue: { name: "Slate Blue", primary: "#1E40AF", dark: "#1E3A8A", bg: "#F8FAFC" },
  green: { name: "Forest Green", primary: "#15803D", dark: "#166534", bg: "#F0FDF4" },
};

export function applyTheme(theme) {
  const t = theme || { preset: "red" };
  const preset = THEMES[t.preset] || THEMES.red;
  const primary = t.preset === "custom" && t.custom_hex ? t.custom_hex : preset.primary;
  const dark = t.preset === "custom" && t.custom_hex ? t.custom_hex : preset.dark;
  const bg = preset.bg;
  const root = document.documentElement;
  root.style.setProperty("--kr-orange", primary);
  root.style.setProperty("--kr-orange-dark", dark);
  root.style.setProperty("--kr-bg", bg);
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [skuPrefixes, setSkuPrefixes] = useState([]);

  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  useEffect(() => {
    api.get("/products").then(r => {
      // Group by SKU name — show only SKUs with an explicit batch prefix.
      const rows = (r.data || [])
        .filter(x => (x.batch_prefix || "").trim())
        .map(x => ({
          id: x.id,
          name: x.name,
          pack: x.pack_size ? `${x.pack_size}${x.pack_unit || ""}` : "",
          sale_price: x.sale_price,
          prefix: (x.batch_prefix || "").trim(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name) || (a.sale_price - b.sale_price));
      setSkuPrefixes(rows);
    }).catch(() => {});
  }, []);

  if (!s) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 border-4 border-zinc-900 border-t-transparent rounded-full animate-spin"></div>
        <div className="text-sm font-medium text-zinc-500 tracking-wide">Loading system settings…</div>
      </div>
    </div>
  );

  const backfillAdvances = async () => {
    if (!window.confirm("Backfill advances? This scans every document with advance_paid > 0 and creates any missing 'Advance paid/received' accounting entries. Safe to run multiple times.")) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/accounting/backfill-advances");
      toast.success(`Scanned ${r.data.docs_scanned} · created ${r.data.advance_entries_created} advance legs · adjusted ${r.data.pending_entries_adjusted} pending entries`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBackfillBusy(false); }
  };

  const updBusiness = (k, v) => setS({ ...s, business: { ...s.business, [k]: v } });
  const updPrefix = (k, field, v) => setS({
    ...s,
    doc_prefixes: { ...s.doc_prefixes, [k]: { ...s.doc_prefixes[k], [field]: v } }
  });
  const updPolicy = (k, v) => setS({ ...s, leave_policy: { ...s.leave_policy, [k]: parseInt(v || 0) } });
  const updTheme = (k, v) => setS({ ...s, theme: { ...s.theme, [k]: v } });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", s);
      applyTheme(s.theme);
      localStorage.setItem("kr_theme", JSON.stringify(s.theme));
      toast.success("Settings saved successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-100 pb-6">
        <PageHeader
          label="ADMINISTRATION CONTROL"
          title="System Settings"
          subtitle="Configure business properties, workflows, structural preferences, and interfaces."
        />
        <div className="flex-shrink-0">
          <button 
            onClick={save} 
            disabled={busy} 
            data-testid="save-settings"
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-lg text-sm font-semibold tracking-wide hover:bg-zinc-800 focus:ring-4 focus:ring-zinc-200 transition-all active:scale-95 disabled:opacity-50"
          >
            <FloppyDisk size={16} weight="bold" className={busy ? "animate-pulse" : ""} /> 
            {busy ? "Saving Configuration…" : "Commit & Save Changes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 items-start">
        {/* Navigation Sidebar Tabs */}
        <div className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0 scrollbar-none border-b lg:border-b-0 border-zinc-100 sticky top-4 z-10 bg-white">
          <button 
            onClick={() => setActiveTab("general")} 
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${activeTab === "general" ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`}
          >
            <Sliders size={18} /> Business Profile
          </button>
          <button 
            onClick={() => setActiveTab("documents")} 
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${activeTab === "documents" ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`}
          >
            <FileText size={18} /> Document Formats
          </button>
          <button 
            onClick={() => setActiveTab("finance")} 
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${activeTab === "finance" ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`}
          >
            <Coins size={18} /> Financials & Expenses
          </button>
          <button 
            onClick={() => setActiveTab("inventory")} 
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${activeTab === "inventory" ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`}
          >
            <Package size={18} /> Low-Stock Targets
          </button>
          <button 
            onClick={() => setActiveTab("theme")} 
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${activeTab === "theme" ? "bg-zinc-100 text-zinc-900 font-bold" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"}`}
          >
            <Palette size={18} /> Appearance Theme
          </button>
        </div>

        {/* Tab Subpanels */}
        <div className="space-y-6">
          {activeTab === "general" && (
            <div className="space-y-6 animate-fadeIn">
              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-6">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Business Profile</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Primary identities and regulatory settings used across invoices and legal forms.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <Field label="Business Name"><Input value={s.business.name} onChange={e => updBusiness("name", e.target.value)} data-testid="biz-name" /></Field>
                  <Field label="GSTIN"><Input value={s.business.gstin} onChange={e => updBusiness("gstin", e.target.value.toUpperCase())} data-testid="biz-gstin" className="font-mono tracking-wider" /></Field>
                  <Field label="Phone"><Input value={s.business.phone} onChange={e => updBusiness("phone", e.target.value)} /></Field>
                  <Field label="Email"><Input type="email" value={s.business.email} onChange={e => updBusiness("email", e.target.value)} /></Field>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Field label="Address" full><Input value={s.business.address} onChange={e => updBusiness("address", e.target.value)} /></Field>
                  </div>
                  <Field label="City"><Input value={s.business.city} onChange={e => updBusiness("city", e.target.value)} /></Field>
                  <Field label="State"><Input value={s.business.state} onChange={e => updBusiness("state", e.target.value)} /></Field>
                  <Field label="Pincode"><Input value={s.business.pincode} onChange={e => updBusiness("pincode", e.target.value)} /></Field>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Field label="Logo URL" full><Input value={s.business.logo_url} onChange={e => updBusiness("logo_url", e.target.value)} className="text-zinc-500 font-mono text-xs" /></Field>
                  </div>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-6">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Leave Policy (per month)</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Monthly leave quota. Leaves within this allowance are paid; anything beyond is auto-deducted at the per-day rate.</p>
                </div>
                <div className="max-w-xs">
                  <Field label="Leave allowance (days / month)">
                    <Input type="number" min="0"
                      value={s.leave_policy?.leave ?? s.leave_policy?.casual ?? 0}
                      onChange={e => updPolicy("leave", e.target.value)}
                      data-testid="lp-leave" />
                  </Field>
                </div>
                <div className="mt-4 p-3 bg-zinc-50 rounded-lg text-xs text-zinc-500 leading-relaxed border border-zinc-100">
                  Leaves within this monthly allowance are paid in full. Anything beyond it is deducted at
                  <span className="font-mono"> (monthly salary ÷ working days)</span>. Half-days pay 0.5 × per-day rate.
                  Special company holidays are always paid — never deducted.
                </div>
              </Card>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6 animate-fadeIn">
              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-6">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Document Numbering Series</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Alters transactional prefixes and sequencing counters safely for future items.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(s.doc_prefixes).map(([key, val]) => (
                    <div key={key} className="bg-zinc-50/50 rounded-xl p-4 border border-zinc-100 flex flex-col justify-between space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-zinc-700 bg-white shadow-sm border border-zinc-200/60 px-2.5 py-1 rounded-md">{key.replace("_", " ")}</span>
                        <div className="text-[11px] font-mono font-semibold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded">
                          Preview: <span className="text-zinc-900">{val.prefix}-{String(val.next).padStart(4, "0")}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Prefix ID"><Input value={val.prefix} onChange={e => updPrefix(key, "prefix", e.target.value)} data-testid={`prefix-${key}`} className="font-mono font-bold" /></Field>
                        <Field label="Next Sequence"><Input type="number" value={val.next} onChange={e => updPrefix(key, "next", parseInt(e.target.value || 1))} data-testid={`next-${key}`} className="font-mono" /></Field>
                      </div>
                      {key === "batch" && (
                        <div className="text-[10px] text-orange-700 bg-orange-50 border-l-2 border-orange-500 p-2 rounded-sm leading-relaxed">
                          This is the <b>fallback</b> batch prefix. Each SKU can override it with its own
                          prefix (e.g. <span className="font-mono">BOO10</span>, <span className="font-mono">BOO40</span>) —
                          set that on the SKU form under <b>Products → New / Edit SKU → Batch numbering</b>.
                          Batches produced for a SKU with its own prefix bypass this counter.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100" data-testid="sku-batch-prefix-summary">
                <div className="border-b border-zinc-100 pb-4 mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-bold text-zinc-900 tracking-tight">SKU-level Batch Prefixes</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      Live view of every SKU with its own batch prefix. Edit on the SKU form in <b>Products</b>.
                      Each prefix runs its own counter — <span className="font-mono">BOO10-0001</span> and <span className="font-mono">BOO40-0001</span> stay independent.
                    </p>
                  </div>
                  <a href="/products" className="text-xs font-mono uppercase tracking-widest text-orange-700 hover:text-orange-900 whitespace-nowrap">
                    Manage SKUs →
                  </a>
                </div>
                {skuPrefixes.length === 0 ? (
                  <div className="text-xs text-zinc-500 py-6 text-center bg-zinc-50/50 rounded-lg border border-dashed border-zinc-200">
                    No SKUs have a custom batch prefix yet — they all use the fallback prefix above.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="kr-table w-full text-xs">
                      <thead><tr><th>Product</th><th>Variant</th><th>Price</th><th>Prefix</th><th>Next batch #</th></tr></thead>
                      <tbody>
                        {skuPrefixes.map(r => (
                          <tr key={r.id} data-testid={`sku-prefix-row-${r.id}`}>
                            <td className="font-bold">{r.name}</td>
                            <td className="text-zinc-600">{r.pack || "—"}</td>
                            <td className="font-mono">₹{r.sale_price}</td>
                            <td className="font-mono font-bold text-orange-700">{r.prefix}</td>
                            <td className="font-mono text-zinc-500">
                              {r.prefix}-{String(
                                (s.doc_prefixes?.batch?.per_prefix?.[r.prefix]) || 1
                              ).padStart(4, "0")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-4">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Purchase Order Line-Item Types</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Control operational types globally. Inventory generation triggers only on specified structural types.</p>
                </div>
                <div className="space-y-3" data-testid="lit-list">
                  {(s.line_item_types || []).map((t, i) => (
                    <div key={i} className="grid grid-cols-12 gap-3 items-center bg-zinc-50/50 p-2.5 rounded-lg border border-zinc-100">
                      <div className="col-span-3">
                        <input
                          className="w-full px-3 py-1.5 border border-zinc-200 rounded-md text-sm font-mono bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none"
                          value={t.value}
                          onChange={e => {
                            const list = [...(s.line_item_types || [])];
                            list[i] = { ...t, value: e.target.value.replace(/[^a-z_]/g, "").toLowerCase() };
                            setS({ ...s, line_item_types: list });
                          }}
                          placeholder="value (a-z_)"
                          data-testid={`lit-value-${i}`}
                        />
                      </div>
                      <div className="col-span-4">
                        <input
                          className="w-full px-3 py-1.5 border border-zinc-200 rounded-md text-sm bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none"
                          value={t.label}
                          onChange={e => {
                            const list = [...(s.line_item_types || [])];
                            list[i] = { ...t, label: e.target.value };
                            setS({ ...s, line_item_types: list });
                          }}
                          placeholder="Label shown in UI dropdown"
                          data-testid={`lit-label-${i}`}
                        />
                      </div>
                      <div className="col-span-3 flex justify-center">
                        <label className="text-xs text-zinc-600 font-medium flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!t.creates_inventory}
                            onChange={e => {
                              const list = [...(s.line_item_types || [])];
                              list[i] = { ...t, creates_inventory: e.target.checked };
                              setS({ ...s, line_item_types: list });
                            }}
                            className="w-4 h-4 accent-zinc-900 border-zinc-300 rounded"
                            data-testid={`lit-inv-${i}`}
                          />
                          Automate Stock
                        </label>
                      </div>
                      <div className="col-span-2 flex justify-end pr-2">
                        <button
                          type="button"
                          onClick={() => {
                            const list = [...(s.line_item_types || [])];
                            list.splice(i, 1);
                            setS({ ...s, line_item_types: list });
                          }}
                          className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-bold transition-colors"
                          data-testid={`lit-del-${i}`}
                        >
                          <Trash size={14} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setS({ ...s, line_item_types: [...(s.line_item_types || []), { value: "", label: "", creates_inventory: false }] })}
                    className="mt-2 inline-flex items-center gap-1.5 text-zinc-900 hover:text-zinc-700 text-xs font-bold uppercase tracking-wider bg-zinc-50 border border-zinc-200 hover:bg-zinc-100 px-4 py-2 rounded-lg transition-all"
                    data-testid="lit-add"
                  >
                    <Plus size={14} weight="bold" /> Add Custom Type
                  </button>
                </div>
              </Card>
            </div>
          )}

          {activeTab === "finance" && (
            <div className="space-y-6 animate-fadeIn">
              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Transactional Defaults</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">System-wide parameters applied fallback structures across sales contexts.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                  <Field label="Standard Fallback GST (%)"><Input type="number" step="0.01" value={s.default_gst_rate} onChange={e => setS({ ...s, default_gst_rate: parseFloat(e.target.value || 0) })} /></Field>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Fixed Operational Monthly Expenses</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Automatic recurring profiles mapped cleanly into standard periodic profit & loss analysis statements.</p>
                </div>
                <div className="space-y-3">
                  {(s.fixed_monthly_expenses || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_180px_auto] gap-4 items-end bg-zinc-50/50 p-3 rounded-xl border border-zinc-100">
                      <Field label={i === 0 ? "Expense Category Name" : ""}>
                        <Input value={row.name} onChange={e => {
                          const list = [...s.fixed_monthly_expenses]; list[i] = { ...row, name: e.target.value };
                          setS({ ...s, fixed_monthly_expenses: list });
                        }} data-testid={`fixed-name-${i}`} />
                      </Field>
                      <Field label={i === 0 ? "Amount (₹)" : ""}>
                        <Input type="number" step="0.01" value={row.amount} onChange={e => {
                          const list = [...s.fixed_monthly_expenses]; list[i] = { ...row, amount: parseFloat(e.target.value || 0) };
                          setS({ ...s, fixed_monthly_expenses: list });
                        }} data-testid={`fixed-amount-${i}`} className="font-mono text-right" />
                      </Field>
                      <button 
                        type="button" 
                        onClick={() => {
                          setS({ ...s, fixed_monthly_expenses: s.fixed_monthly_expenses.filter((_, x) => x !== i) });
                        }} 
                        className="text-red-500 hover:text-red-700 pb-2.5 text-xs uppercase font-bold flex items-center gap-1 transition-colors"
                      >
                        <Trash size={14} /> Delete
                      </button>
                    </div>
                  ))}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-3 mt-4 border-t border-zinc-100">
                    <button 
                      type="button" 
                      onClick={() => {
                        setS({ ...s, fixed_monthly_expenses: [...(s.fixed_monthly_expenses || []), { name: "", amount: 0 }] });
                      }} 
                      className="inline-flex items-center gap-1.5 text-zinc-950 hover:text-zinc-700 text-xs font-bold uppercase tracking-wider bg-zinc-50 border border-zinc-200 px-4 py-2 rounded-md transition-all"
                      data-testid="add-fixed"
                    >
                      <Plus size={14} weight="bold" /> Add Operating Expense
                    </button>
                    <div className="font-mono text-sm bg-zinc-900 text-white px-4 py-2 rounded-lg flex items-center justify-between gap-6">
                      <span className="text-zinc-400 text-xs font-sans font-medium uppercase tracking-wider">Aggregate Matrix Target</span>
                      <span className="font-bold text-base">₹{(s.fixed_monthly_expenses || []).reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-4">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Ledger & Revenue Channels</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Define account hooks and asset logs used inside internal transactional workflows.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <SourceList
                    title="Payment Sources"
                    hint="Cash · Bank · UPI · Card"
                    items={s.payment_sources || []}
                    onChange={(next) => setS({ ...s, payment_sources: next })}
                    testIdPrefix="pay-src"
                  />
                  <SourceList
                    title="Income Sources"
                    hint="Contributions · Loans · Retained Earnings"
                    items={s.income_sources || []}
                    onChange={(next) => setS({ ...s, income_sources: next })}
                    testIdPrefix="inc-src"
                  />
                </div>
              </Card>

              <Card className="shadow-sm border-2 border-dashed border-zinc-200 bg-zinc-50/30">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-zinc-900 text-white rounded-xl shadow-sm">
                    <ShieldCheck size={24} />
                  </div>
                  <div className="space-y-2 flex-1">
                    <h3 className="text-base font-bold text-zinc-900 tracking-tight">System Ledger Maintenance Tools</h3>
                    <p className="text-xs text-zinc-500 leading-relaxed max-w-2xl">
                      Performs a multi-pass reconciliation audit scanning database records featuring an outstanding balance. 
                      Safely injects structural balancing elements back matching latest multi-leg structures while cleanly ensuring zero structural duplication.
                    </p>
                    <div className="pt-2">
                      <button 
                        onClick={backfillAdvances} 
                        disabled={backfillBusy} 
                        data-testid="backfill-advances-btn"
                        className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-300 font-semibold px-4 py-2 rounded-lg text-xs tracking-wide shadow-sm transition-all active:scale-95 disabled:opacity-50"
                      >
                        {backfillBusy ? "Rebuilding Ledger States…" : "Initiate Backfill Run Automation"}
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === "inventory" && (
            <div className="space-y-6 animate-fadeIn">
              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Global Low-Stock Threshold fallback</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Fallback target values evaluated when dedicated specific row limits are absent.</p>
                </div>
                <div className="max-w-xs">
                  <Field label="Global Default Threshold">
                    <Input type="number" step="0.01"
                           value={s.default_reorder_point ?? 0}
                           onChange={e => setS({ ...s, default_reorder_point: parseFloat(e.target.value || 0) })}
                           data-testid="default-rop-input" className="font-mono" />
                  </Field>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Leftover Bag Alert</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Warn on Inventory when a partially-opened raw material bag has been sitting on the floor for more than this many days without full consumption.</p>
                </div>
                <div className="max-w-xs">
                  <Field label="Alert after (days)">
                    <Input type="number" step="1" min="0"
                           value={s.leftover_alert_days ?? 7}
                           onChange={e => setS({ ...s, leftover_alert_days: parseInt(e.target.value || 0) })}
                           data-testid="leftover-alert-days-input" className="font-mono" />
                  </Field>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Batch Yield — Max Acceptable Wastage</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Batches whose <code className="text-orange-600">wastage_kg ÷ total_weight_used</code> exceeds this percentage will be flagged in red on Batches list and Details.</p>
                </div>
                <div className="max-w-xs">
                  <Field label="Max wastage (%)">
                    <Input type="number" step="0.1" min="0" max="100"
                           value={s.max_wastage_pct ?? 5}
                           onChange={e => setS({ ...s, max_wastage_pct: parseFloat(e.target.value || 0) })}
                           data-testid="max-wastage-pct-input" className="font-mono" />
                  </Field>
                </div>
              </Card>

              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-5">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">Pool Queue Escalation</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Turns the top-nav "pool bags" pill RED (and pushes a one-per-day admin notification) when either limit is exceeded.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
                  <Field label="Max unsplit pool bags">
                    <Input type="number" step="1" min="0"
                           value={s.pool_queue_max ?? 5}
                           onChange={e => setS({ ...s, pool_queue_max: parseInt(e.target.value || 0) })}
                           data-testid="pool-queue-max-input" className="font-mono" />
                  </Field>
                  <Field label="Escalate after (days)">
                    <Input type="number" step="1" min="0"
                           value={s.pool_queue_age_days ?? 3}
                           onChange={e => setS({ ...s, pool_queue_age_days: parseInt(e.target.value || 0) })}
                           data-testid="pool-queue-age-days-input" className="font-mono" />
                  </Field>
                </div>
              </Card>

              <RawMaterialThresholds />
            </div>
          )}

          {activeTab === "theme" && (
            <div className="space-y-6 animate-fadeIn">
              <Card className="shadow-sm border border-zinc-100">
                <div className="border-b border-zinc-100 pb-4 mb-6">
                  <h3 className="text-base font-bold text-zinc-900 tracking-tight">System Theme Customization</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Alter visual branding accents across application layers.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-8">
                  {/* Preset Pickers */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                    {Object.entries(THEMES).map(([key, t]) => (
                      <button key={key}
                        type="button"
                        onClick={() => { updTheme("preset", key); }}
                        data-testid={`theme-${key}`}
                        className={`group p-4 border-2 text-left rounded-xl transition-all ${s.theme?.preset === key ? "border-zinc-900 bg-zinc-50/50 shadow-sm" : "border-zinc-100 hover:border-zinc-300 bg-white"}`}>
                        <div className="w-full h-12 mb-3 rounded-lg shadow-inner transition-transform group-hover:scale-[1.02]" style={{ background: t.primary }} />
                        <div className="font-bold text-sm text-zinc-900">{t.name}</div>
                        <div className="font-mono text-[11px] text-zinc-400 mt-0.5">{t.primary}</div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => { updTheme("preset", "custom"); }}
                      className={`p-4 border-2 text-left rounded-xl transition-all relative ${s.theme?.preset === "custom" ? "border-zinc-900 bg-zinc-50/50 shadow-sm" : "border-zinc-100 hover:border-zinc-300 bg-white"}`}
                    >
                      <div className="w-full h-12 mb-3 rounded-lg shadow-inner flex items-center justify-center overflow-hidden" style={{ background: s.theme?.custom_hex || "#888" }}>
                        <input type="color" value={s.theme?.custom_hex || "#B91C1C"}
                          onChange={e => { updTheme("custom_hex", e.target.value); updTheme("preset", "custom"); }}
                          data-testid="theme-custom"
                          className="w-full h-full scale-150 cursor-pointer opacity-0 absolute inset-0" />
                        <span className="text-xs bg-white/90 backdrop-blur px-2 py-0.5 rounded font-bold text-zinc-900 pointer-events-none shadow-sm">Pick Color</span>
                      </div>
                      <div className="font-bold text-sm text-zinc-900">Custom Accent</div>
                      <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{s.theme?.custom_hex || "#—"}</div>
                    </button>
                  </div>

                  {/* Interactive Dynamic Theme Preview Frame Mockup */}
                  <div className="bg-zinc-900 rounded-xl p-5 text-white flex flex-col justify-between shadow-xl ring-1 ring-black/10">
                    <div className="space-y-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
                        <span className="text-[10px] font-mono text-zinc-500 ml-2 tracking-widest uppercase">Live Accent Target Mockup</span>
                      </div>
                      <div className="bg-zinc-800/80 border border-zinc-700/50 rounded-lg p-3 space-y-2">
                        <div className="h-2 w-1/3 bg-zinc-600 rounded"></div>
                        <div className="h-4 w-3/4 bg-zinc-500 rounded mt-1"></div>
                        <div className="h-8 w-full rounded-md mt-4 flex items-center justify-center text-[11px] font-bold tracking-wider uppercase transition-colors" 
                             style={{ backgroundColor: s.theme?.preset === "custom" ? s.theme?.custom_hex : (THEMES[s.theme?.preset]?.primary || THEMES.red.primary) }}>
                          Active Button Accent
                        </div>
                      </div>
                    </div>
                    <div className="text-[10px] text-zinc-400 font-mono mt-8 text-center bg-zinc-800 py-1.5 rounded border border-zinc-700/40">
                      Preset Profile: <span className="text-white uppercase font-bold">{s.theme?.preset || "red"}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceList({ title, hint, items, onChange, testIdPrefix }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div className="bg-white p-4 rounded-xl border border-zinc-100 flex flex-col justify-between space-y-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-800">{title}</div>
        <div className="text-[11px] text-zinc-400 mt-0.5">Supported structures: {hint}</div>
      </div>
      
      <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1 scrollbar-thin">
        {items.map((it, idx) => (
          <div key={idx} className="flex items-center gap-2 group animate-fadeIn" data-testid={`${testIdPrefix}-row-${idx}`}>
            <input
              value={it}
              onChange={(e) => {
                const next = [...items];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="flex-1 px-3 py-1.5 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950 text-sm bg-zinc-50/40 hover:bg-zinc-50 focus:bg-white transition-all font-medium"
            />
            <button
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-zinc-400 hover:text-red-500 w-7 h-7 inline-flex items-center justify-center rounded-md hover:bg-red-50 transition-all text-xs"
              data-testid={`${testIdPrefix}-del-${idx}`}
            >
              ✕
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-zinc-400 italic py-4 text-center border border-dashed border-zinc-200 rounded-lg">No entries mapped yet</div>
        )}
      </div>

      <div className="flex gap-2 pt-2 border-t border-zinc-100">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="New profile context identifier…"
          className="flex-1 px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 transition-all"
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          onClick={add}
          className="px-4 py-1.5 bg-zinc-950 text-white text-xs font-bold uppercase tracking-wider rounded-lg hover:bg-zinc-800 active:scale-95 transition-all flex-shrink-0"
          data-testid={`${testIdPrefix}-add`}
        >
          Append
        </button>
      </div>
    </div>
  );
}

function RawMaterialThresholds() {
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState({});
  const [loaded, setLoaded] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/raw-materials");
      setRows(r.data.map(m => ({ ...m, _draft: m.reorder_point ?? "" })));
      setLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load raw materials");
      setLoaded(true);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    setBusy(b => ({ ...b, [row.id]: true }));
    try {
      const val = row._draft === "" ? null : parseFloat(row._draft);
      await api.put(`/raw-materials/${row.id}`, { reorder_point: val });
      toast.success(`Saved · ${row.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(b => ({ ...b, [row.id]: false }));
    }
  };

  if (!loaded) return null;

  return (
    <Card className="shadow-sm border border-zinc-100">
      <div className="border-b border-zinc-100 pb-4 mb-4">
        <h3 className="text-base font-bold text-zinc-900 tracking-tight">Per-Material Low-Stock Threshold Limits</h3>
        <p className="text-xs text-zinc-500 mt-0.5">Explicit reorder properties driving inventory warning systems.</p>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 font-mono italic py-6 text-center border border-dashed border-zinc-200 rounded-xl bg-zinc-50/50">
          No active catalog raw materials discovered. Use inventory profiles to seed records.
        </div>
      ) : (
        <div className="border border-zinc-100 rounded-xl overflow-hidden divide-y divide-zinc-100" data-testid="rop-list">
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-[1fr_auto_140px_100px] gap-4 items-center p-3.5 bg-white hover:bg-zinc-50/60 transition-colors">
              <div>
                <div className="font-semibold text-sm text-zinc-900">{r.name}</div>
                <div className="text-[11px] font-mono text-zinc-400 mt-0.5">
                  Code: <span className="text-zinc-600 font-medium">{r.code}</span> · Metrics: <span className="text-zinc-600 font-medium">{r.unit || "kg"}</span>
                </div>
              </div>
              <div className="text-xs font-mono font-medium text-zinc-400 text-right uppercase tracking-wider pr-2">
                Active Limit
              </div>
              <input
                type="number"
                step="0.01"
                value={r._draft}
                placeholder="Global Default"
                onChange={e => setRows(rs => rs.map(x => x.id === r.id ? { ...x, _draft: e.target.value } : x))}
                className="w-full px-3 py-1.5 border border-zinc-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950 text-right bg-zinc-50/30 hover:bg-zinc-50 focus:bg-white transition-all"
                data-testid={`rop-input-${r.code}`}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => save(r)}
                  disabled={busy[r.id]}
                  className="px-4 py-1.5 bg-zinc-950 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider rounded-md disabled:opacity-40 transition-all active:scale-95"
                  data-testid={`rop-save-${r.code}`}
                >
                  {busy[r.id] ? "Syncing…" : "Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}